import React, { useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

interface Notice {
  id: number;
  severity: string;
  createdAt: string;
  source: string;
  message: string;
  title?: string;
  link?: string;
}

interface Props {
  userNotice: Notice[];
  refreshUserHeder: () => void;
}

function getIcon(notice: Notice): string {
  const s = (notice.source || '').toLowerCase();
  if (s.includes('timesheet') || s.includes('report')) return '⏱';
  if (s.includes('expense') || s.includes('finance')) return '💰';
  if (s.includes('leave') || s.includes('absence')) return '🌴';
  if (s.includes('training') || s.includes('course')) return '🎓';
  if (s.includes('booking') || s.includes('room')) return '📅';
  if (s.includes('document')) return '📄';
  if (notice.severity === 'success') return '✅';
  if (notice.severity === 'error') return '❌';
  return '🔔';
}

function timeAgo(dateStr: string): string {
  const m = moment.utc(dateStr);
  const diff = moment().diff(m, 'minutes');
  if (diff < 1) return 'just now';
  if (diff < 60) return `${diff}m`;
  const h = Math.floor(diff / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

const NotifTab: React.FC<Props> = ({ userNotice, refreshUserHeder }) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());

  const handleDismiss = async (id: number) => {
    try {
      setDismissed(prev => new Set(prev).add(id));
      await axios.delete(`notice/${id}`);
      setTimeout(refreshUserHeder, 400);
    } catch {
      enqueueSnackbar('Error dismissing notice', { variant: 'error' });
    }
  };

  const handleView = (notice: Notice) => {
    if (notice.link) navigate(notice.link);
  };

  const visible = userNotice.filter(n => !dismissed.has(n.id));

  if (!visible.length) {
    return (
      <div style={{ padding: '32px 12px', textAlign: 'center', color: 'var(--text-color-calendar-week)' }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>✓</div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>All caught up</div>
        <div style={{ fontSize: 11, marginTop: 4 }}>No new notifications</div>
      </div>
    );
  }

  return (
    <div>
      {visible.map(notice => (
        <div key={notice.id} className="nc">
          <div className="nc-top">
            <div className="nc-unread" />
            <div className="nc-icon">{getIcon(notice)}</div>
            <div className="nc-title">{notice.source || 'Notification'}</div>
            <div className="nc-time">{timeAgo(notice.createdAt)}</div>
          </div>
          <div className="nc-body">{notice.message}</div>
          {notice.title && (
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color)', margin: '4px 0 0', paddingLeft: 2 }}>
              {notice.title}
            </div>
          )}
          <div className="nc-actions">
            {notice.link && (
              <button
                type="button"
                onClick={() => handleView(notice)}
                style={{ flex: 1, padding: '5px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: 'rgba(37,99,235,.08)', color: '#2563eb', border: '1px solid rgba(37,99,235,.2)', transition: 'all .15s' }}
              >
                View →
              </button>
            )}
            <button
              type="button"
              onClick={() => handleDismiss(notice.id)}
              style={{ flex: 1, padding: '5px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: 'transparent', color: 'var(--text-color-calendar)', border: '1px solid var(--border-new)', transition: 'all .15s' }}
            >
              Dismiss
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotifTab;
